import { useGetProfile } from '@/api/generated/settings/settings.ts';
import { UserMenuNotificationsSub } from '@/components/layout/UserMenuNotificationsSub';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemePicker } from '@/components/ui/theme-picker';
import { useAuth } from '@/features/auth';
import { useNotifications } from '@/hooks/useNotifications';
import { availableLocales, localeLabels, type Locale } from '@/i18n/i18n';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { Languages, LogOut, Settings } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useNavigate } from 'react-router-dom';

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1]!;
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

export const UserMenu = () => {
  const { t, locale, setLocale } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();

  const { logout } = useAuth();
  const { data: settings, isLoading } = useGetProfile();

  const userInfo = settings?.personalInfo;
  const displayName = userInfo
    ? `${userInfo.firstName} ${userInfo.lastName}`
    : isLoading && !settings
      ? t('common.loading')
      : 'User';
  const displayEmail = userInfo?.email || '';
  const initials = getInitials(displayName);

  const handleLogout = () => {
    logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='group/user-menu relative size-8 shrink-0 cursor-pointer rounded-full p-0 hover:bg-transparent data-[state=open]:bg-transparent'
          aria-label={displayName}
        >
          <Avatar className='size-7 rounded-full'>
            <AvatarFallback
              className={cn(
                'rounded-full bg-transparent text-[11px] font-medium transition-colors',
                'group-hover/user-menu:bg-accent group-hover/user-menu:text-accent-foreground',
                'group-data-[state=open]/user-menu:bg-accent group-data-[state=open]/user-menu:text-accent-foreground'
              )}
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {unreadCount > 0 && (
            <span
              className='bg-fin-danger ring-background absolute right-0 bottom-0 size-2.5 rounded-full ring-2'
              aria-hidden
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-60' align='end' sideOffset={8}>
        <DropdownMenuLabel className='flex items-center gap-2 p-2 font-normal'>
          <Avatar className='size-9 rounded-full'>
            <AvatarFallback className='rounded-full text-xs font-medium'>{initials}</AvatarFallback>
          </Avatar>
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span className='truncate text-sm font-medium'>{displayName}</span>
            {displayEmail && (
              <span className='text-muted-foreground truncate text-xs font-normal'>
                {displayEmail}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className='px-2 py-1.5'>
          <ThemePicker value={theme} onChange={setTheme} preventFocusSteal />
        </div>

        <DropdownMenuItem onSelect={() => navigate('/settings')} className='gap-2'>
          <Settings className='size-4' />
          {t('nav.settings')}
        </DropdownMenuItem>

        <UserMenuNotificationsSub />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className='gap-2'>
            <Languages className='size-4' />
            {t('userMenu.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={locale}
              onValueChange={(value) => setLocale(value as Locale)}
            >
              {availableLocales.map((code) => (
                <DropdownMenuRadioItem key={code} value={code}>
                  {localeLabels[code]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} className='gap-2'>
          <LogOut className='size-4' />
          {t('nav.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
